import { useEffect, useRef, useState } from 'react';

/**
 * Espera antes de anunciar una carga completa. La región se monta con la vista y la primera carga
 * arranca en ese mismo render: rellenarla en el efecto cae en el mismo fotograma y no hay mutación
 * que anunciar (React 17, medido en TAN-10529). De paso calla las cargas que se resuelven al momento.
 */
export const LOADING_ANNOUNCE_DELAY_MS = 150;

/**
 * Textos que la vista sirve a la región de estado. Llegan ya traducidos e interpolados:
 * el hook decide CUÁNDO se anuncia cada uno, nunca cómo se redacta.
 */
export type ListStatusMessages = {
  /** Lista resuelta sin resultados. */
  empty: string;
  /** Carga inicial o recarga completa: la lista se ha sustituido por su skeleton. */
  loading: string;
  /** Se está cargando una página adicional sin desmontar la lista ya visible. */
  loadingMore: string;
  /** Lista resuelta con resultados (el número ya va dentro del texto). */
  showing: string;
};

export type UseListStatusMessageParams = {
  /**
   * Identifica el filtro activo (pestaña + filtros de la vista). Al cambiar de valor se vuelve
   * a anunciar aunque el número de ítems sea el mismo: el usuario ha pedido otra cosa.
   */
  filterKey: string;
  /** Quedan páginas por pedir: una lista vacía todavía puede dejar de estarlo. */
  hasMore?: boolean;
  /** Carga inicial o recarga completa: la lista se sustituye por su skeleton. */
  isLoading: boolean;
  /** Paginación: la lista sigue montada y se le añaden ítems al final. */
  isLoadingMore: boolean;
  /** Número de ítems que la lista muestra ahora mismo. */
  itemCount: number;
  messages: ListStatusMessages;
};

/**
 * Deriva el mensaje de una región de estado (`role="status"`) para una lista con scroll infinito
 * (WCAG 4.1.3). Es el motor del patrón «scroll infinito accesible»: la vista monta SIEMPRE la
 * región (una que nace con su contenido no produce mutación y no se anuncia) y este hook le pasa
 * el texto que toca en cada momento.
 *
 * Reglas de anuncio:
 * - Mientras se pagina, el mensaje es el de «cargando más»: el skeleton que se pinta va
 *   `aria-hidden`, así que esta es la única señal que llega a un lector de pantalla.
 * - Al resolverse una carga se anuncia el resultado (con ítems o vacío) solo si cambió el número
 *   de ítems o el filtro, o si venía de paginar. Un re-render que no cambia nada no anuncia nada.
 * - Una lista vacía con páginas pendientes (`hasMore`) NO se anuncia como vacía: el filtro es de
 *   cliente sobre lo ya paginado, así que ese cero es «todavía no ha llegado», no «no hay».
 * - Una carga completa se anuncia solo si dura más de `LOADING_ANNOUNCE_DELAY_MS`; hasta entonces
 *   se mantiene el mensaje anterior. El skeleton va `aria-hidden`: la señal es esta región.
 * - Tras anunciar una carga, su resultado se anuncia siempre, aunque coincida con el anterior: si no,
 *   la región se quedaría diciendo «cargando» (TAN-10529).
 * - No se anuncia nada hasta que ha empezado una primera carga, para que el render inicial —lista
 *   vacía porque todavía no se ha pedido nada— no se lea como «no hay resultados».
 *
 * @returns el mensaje que la región de estado debe contener ahora mismo
 */
export default function useListStatusMessage({
  filterKey,
  hasMore = false,
  isLoading,
  isLoadingMore,
  itemCount,
  messages,
}: UseListStatusMessageParams): string {
  const { empty, loading, loadingMore, showing } = messages;

  const [message, setMessage] = useState<string>('');
  const lastAnnouncedKeyRef = useRef<string | null>(null);
  const hasStartedLoadingRef = useRef<boolean>(false);
  const wasLoadingMoreRef = useRef<boolean>(false);
  const wasLoadingAnnouncedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isLoading) return undefined;

    const timeoutId = setTimeout(() => {
      wasLoadingAnnouncedRef.current = true;
      setMessage(loading);
    }, LOADING_ANNOUNCE_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading, loading]);

  useEffect(() => {
    if (isLoading || isLoadingMore) hasStartedLoadingRef.current = true;

    if (isLoadingMore) {
      wasLoadingMoreRef.current = true;
      setMessage(loadingMore);
      return;
    }

    if (isLoading || !hasStartedLoadingRef.current) return;

    // Vacío con páginas pendientes: la vista sigue pidiendo, así que ni se anuncia «no hay» ni se
    // consume el `wasLoadingMore`, para que el resultado real se anuncie cuando llegue.
    if (itemCount === 0 && hasMore) return;

    const resolvedKey = `${filterKey}|${itemCount}`;
    // Tras paginar o anunciar una carga siempre se reescribe el mensaje aunque el resultado sea
    // idéntico: si no, la región se quedaría diciendo «cargando» cuando la lista no cambia.
    const shouldAnnounce = wasLoadingMoreRef.current || wasLoadingAnnouncedRef.current || lastAnnouncedKeyRef.current !== resolvedKey;
    wasLoadingMoreRef.current = false;
    wasLoadingAnnouncedRef.current = false;
    if (!shouldAnnounce) return;

    lastAnnouncedKeyRef.current = resolvedKey;
    setMessage(itemCount === 0 ? empty : showing);
  }, [empty, filterKey, hasMore, isLoading, isLoadingMore, itemCount, loadingMore, showing]);

  return message;
}
